import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { addProductWarehouseAreaSchema } from 'modules/inventory/settings/warehouse-area/schemas/add-product-warehouse-area.schema';
import { IStock } from 'modules/inventory/warehouse/interfaces';
import { STOCK_ACTIVITY_OPERATIONS } from 'modules/inventory/product/constants/stock-operations.constants';
import { StockService } from 'modules/inventory/warehouse/services';
import { PRODUCTS_WAREHOUSE_STOCK } from 'modules/inventory/product/constants/query-keys';
import { PRODUCTS_ONE_KEY } from 'modules/inventory/product/constants';
import { REPORT_PRODUCT_STOCK_ACTIVITY } from 'modules/reports/constants/query-keys';
import { PROVIDER_TYPE_ENUM } from 'modules/inventory/provider/common/constants';

const initValues: IStock = {
  warehouse: '',
  quantity: 1,
  cause: undefined,
  warehouseArea: '',
  providerType: undefined,
  responsible: '',
  evidence: [],
  operation: STOCK_ACTIVITY_OPERATIONS.ADDED,
};

const useAddAvailableProductStockForm = (
  productId: string,
  onClose: () => void,
  defaultValues: IStock = initValues,
) => {
  const { t } = useTranslation('product');
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, watch, setValue, formState } = useForm({
    resolver: yupResolver(addProductWarehouseAreaSchema),
    defaultValues,
  });

  const actualQuantity = watch('quantity');
  const operation = watch('operation');

  useEffect(() => {
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  const finalQuantity = useCallback(
    (currentStock: number) => {
      switch (operation) {
        case STOCK_ACTIVITY_OPERATIONS.ADDED:
          return currentStock + Number(actualQuantity);
        case STOCK_ACTIVITY_OPERATIONS.DISCOUNTED:
          return currentStock - Number(actualQuantity);
        default:
          break;
      }
    },
    [operation, actualQuantity],
  );

  useEffect(() => {
    // @ts-ignore
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  // @ts-ignore
  const { mutate, error, isPending: isLoading, isSuccess, data } = useMutation(
    // @ts-ignore
    (values: IStock) => StockService.setStockByProductId(
      productId,
      {
        ...values,
        cause: values?.cause?._id,
        responsible: values?.providerType === PROVIDER_TYPE_ENUM.CARRIER && values?.driverId ? values.driverId : values?.responsible,
        providerType: values?.providerType === PROVIDER_TYPE_ENUM.CARRIER && values?.driverId ? 'DRIVER' : values?.providerType,
      }),
    {
      onSuccess: (data, values) => {
        queryClient.invalidateQueries({ queryKey: [PRODUCTS_WAREHOUSE_STOCK] });
        queryClient.invalidateQueries({ queryKey: [productId, PRODUCTS_ONE_KEY] });
        queryClient.invalidateQueries({ queryKey: [REPORT_PRODUCT_STOCK_ACTIVITY] });
        values?._id && queryClient.invalidateQueries({ queryKey: [values._id] });
        toast.success(t(values?._id ? 'updateStockSuccess' : 'createdStockSuccess'));
        onClose?.();
        reset();
      },
    },
  );

  return {
    control,
    error,
    isLoading,
    formState,
    isSuccess,
    data,
    setValue,
    watch,
    quantity: {
      actualQuantity,
      finalQuantity,
    },
    operation,
    reset,
    // @ts-ignore
    onSubmit: handleSubmit((values) => {
      mutate(values);
      // mutate({ ...values, ...((values?.evidence?.length as number) > 0 ? values.evidence?.[0] : null) });
    }),
  };
};
export default useAddAvailableProductStockForm;
